import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ClipboardList, Loader2, Trash2, Truck, Package, Mail, Calendar } from "lucide-react";

const STATUSES = ["inviato", "confermato", "in_consegna", "consegnato", "annullato"];
const STATUS_LBL = { inviato: "Inviato", confermato: "Confermato", in_consegna: "In consegna", consegnato: "Consegnato", annullato: "Annullato" };
const STATUS_CLS = {
  inviato: "bg-sky-500/10 text-sky-400 border-sky-500/30",
  confermato: "bg-indigo-500/10 text-indigo-400 border-indigo-500/30",
  in_consegna: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  consegnato: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  annullato: "bg-rose-500/10 text-rose-400 border-rose-500/30",
};

export default function Ordini() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/orders"); setOrders(r.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (oid, status) => {
    try {
      await api.patch(`/orders/${oid}/status`, { status });
      toast.success("Stato aggiornato.");
      load();
    } catch (e) { toast.error("Errore aggiornamento stato."); }
  };

  const del = async (oid) => {
    if (!window.confirm("Eliminare questo ordine dallo storico?")) return;
    await api.delete(`/orders/${oid}`);
    toast.success("Ordine eliminato.");
    load();
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Storico Ordini</h1>
        <p className="text-slate-500 text-sm mt-1">Ordini di riordino inviati ai fornitori, con data e stato di consegna.</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : orders.length === 0 ? (
        <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-10 text-center" data-testid="orders-empty">
          <ClipboardList className="h-10 w-10 mx-auto text-slate-500 mb-3" />
          <p className="text-slate-300 font-medium">Nessun ordine inviato</p>
          <p className="text-slate-500 text-sm mt-1">Invia un ordine dalla pagina Report Riordini con "Invio Automatico Ordine".</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <div key={o.id} data-testid={`order-${o.id}`} className="rounded-lg border border-slate-800 bg-slate-900/60 overflow-hidden">
              <div className="flex flex-wrap items-center gap-3 px-5 py-4 bg-slate-900/80">
                <div className="h-10 w-10 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center shrink-0">
                  <Truck className="h-5 w-5 text-amber-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-heading font-bold truncate">{o.supplier_name}</div>
                  <div className="text-xs text-slate-500 font-mono-data flex flex-wrap items-center gap-x-3 gap-y-0.5">
                    {o.supplier_email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{o.supplier_email}</span>}
                    <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{(o.created_at || "").slice(0, 10)}</span>
                    <span className="flex items-center gap-1"><Package className="h-3 w-3" />{(o.items || []).length} articoli</span>
                    {o.email_sent
                      ? <span className="text-emerald-400">email inviata</span>
                      : <span className="text-rose-400">email non inviata</span>}
                    {o.restocked && <span data-testid={`order-restocked-${o.id}`} className="text-emerald-400">giacenza ricaricata</span>}
                  </div>
                </div>
                <select
                  data-testid={`order-status-${o.id}`}
                  value={o.status}
                  onChange={(e) => setStatus(o.id, e.target.value)}
                  className={`h-9 rounded-lg border px-3 text-xs font-semibold outline-none ${STATUS_CLS[o.status] || "bg-slate-800 text-slate-300 border-slate-700"}`}
                >
                  {STATUSES.map((s) => <option key={s} value={s} className="bg-slate-900 text-slate-100">{STATUS_LBL[s]}</option>)}
                </select>
                <button onClick={() => del(o.id)} data-testid={`order-delete-${o.id}`} className="p-2 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-slate-800"><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="px-5 py-3">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] font-mono-data uppercase tracking-wider text-slate-500 border-b border-slate-800">
                        <th className="py-2 pr-4">Articolo</th>
                        <th className="py-2 pr-4">Cod. Prodotto</th>
                        <th className="py-2 pr-4">Da ordinare</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(o.items || []).map((it, idx) => (
                        <tr key={idx} className="border-b border-slate-800/40 last:border-0">
                          <td className="py-2 pr-4">{it.name}</td>
                          <td className="py-2 pr-4 font-mono-data text-amber-400">{it.codice_prodotto}</td>
                          <td className="py-2 pr-4 font-mono-data font-bold">{it.to_order}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
